import { tokens } from '@kdk/shared/theme';
import { Box, ListItemButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef, type MouseEvent } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { LoadingStatus } from '../shared-ui/LoadingStatus';
import { PageSkeleton } from '../shared-ui/skeletons';
import { TableScroll } from '../shared-ui/TableScroll';
import { DrawerHost } from '../features/drawers/DrawerHost';
import type { NavItem } from './navItems';
import { UserBox } from './UserBox';
import { usePendingNav } from './usePendingNav';

type Section = { title: string; items: NavItem[] };

const groupBySection = (items: readonly NavItem[]): Section[] =>
  items.reduce<Section[]>((sections, item) => {
    const last = sections.at(-1);
    if (last?.title === item.section) {
      last.items.push(item);
      return sections;
    }
    return [...sections, { title: item.section, items: [item] }];
  }, []);

/** メニュー項目が、いまのページ(または移動中の先)か。NavLink の `end` と同じ判定。 */
const isCurrent = (to: string, pathname: string): boolean =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

/** 修飾キー付き・中クリックなど、同じタブでの移動にならないクリック。 */
const isPlainClick = (event: MouseEvent<HTMLElement>): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

export const AppShell = ({ items }: { items: readonly NavItem[] }) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  // sm 以上(PC)ではメニューを縦スクロール領域に入れる。スマホは横並びの帯なので包まない
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const mainRef = useRef<HTMLElement>(null);
  const { pendingTo, setPendingTo, navigating } = usePendingNav(pathname);

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);

  const menuSections = (
    <>
      {groupBySection(items).map((section, index) => (
        <Box key={section.title} sx={{ display: 'contents' }}>
          {index > 0 && (
            // スマホ(横並びメニュー)だけのグループ区切り(縦線)。PC ではラベルの上の余白が区切りを担う
            <Box
              role="separator"
              sx={{
                display: { xs: 'block', sm: 'none' },
                flex: 'none',
                alignSelf: 'stretch',
                bgcolor: tokens.sidebarLine,
                width: '1px',
                mx: '6px',
              }}
            />
          )}
          {/* グループ見出し。メニュー項目と見分けがつくよう「ラベル」の見た目にする:
                小さく・暗く・字間を広げる(罫線は引かない)。操作できないのでカーソルは矢印、選択も不可。 */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: '8px',
              px: 1,
              pt: index === 0 ? '14px' : '18px',
              pb: '6px',
            }}
          >
            <Typography
              sx={{
                color: tokens.sidebarDim,
                fontSize: 11,
                letterSpacing: '0.08em',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                cursor: 'default',
                userSelect: 'none',
              }}
            >
              {section.title}
            </Typography>
          </Box>
          {section.items.map((item) => (
            <ListItemButton
              key={item.to}
              draggable={false}
              component={NavLink}
              to={item.to}
              end={item.to === '/'}
              // 見た目の強調は「移動中ならその先、そうでなければ今のページ」。aria-current は実際の URL(NavLink)に従う
              data-selected={
                pendingTo === null ? isCurrent(item.to, pathname) : item.to === pendingTo
              }
              onClick={(event: MouseEvent<HTMLElement>) => {
                if (isPlainClick(event) && !isCurrent(item.to, pathname)) setPendingTo(item.to);
              }}
              sx={{
                flex: 'none',
                // 見出しより右に字下げして、「見出しの下にぶら下がる項目」であることを示す
                ml: { sm: '8px' },
                borderRadius: '6px',
                px: '10px',
                py: '9px',
                fontSize: 14,
                whiteSpace: 'nowrap',
                color: tokens.sidebarMu,
                '&:hover': { bgcolor: tokens.sidebarHover, color: tokens.sidebarTx },
                '&[data-selected="true"]': {
                  bgcolor: tokens.sidebarActive,
                  color: tokens.sidebarTx,
                  boxShadow: `inset 3px 0 0 ${tokens.sidebarAccent}`,
                },
              }}
            >
              {item.label}
            </ListItemButton>
          ))}
        </Box>
      ))}
    </>
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '232px 1fr' },
        gridTemplateRows: { xs: 'auto 1fr', sm: '1fr' },
        height: '100%',
      }}
    >
      <Box
        component="nav"
        aria-label="メインメニュー"
        sx={{
          bgcolor: tokens.sidebar,
          borderRight: { sm: `1px solid ${tokens.sidebarLine}` },
          borderBottom: { xs: `1px solid ${tokens.sidebarLine}`, sm: 0 },
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          gap: '4px',
          // PC: 見出し・メニュー・下部を固定し、スクロールはメニュー領域だけにする。スマホ: 横並びなので従来どおり nav が横スクロール
          overflow: { xs: 'auto', sm: 'hidden' },
          p: { xs: '8px', sm: '20px 14px' },
          // 全体の焦点リングは主題色。ライトでは主題色がサイドバーの背景と同じ濃い青で見えないので、
          // サイドバーの中だけ専用の色にする
          '& :focus-visible': { outlineColor: tokens.sidebarAccent },
        }}
      >
        <Box
          sx={{
            display: { xs: 'none', sm: 'block' },
            px: 1,
            pb: '18px',
            mb: '12px',
            borderBottom: `1px solid ${tokens.sidebarLine}`,
          }}
        >
          <Typography
            sx={{ color: tokens.sidebarTx, fontSize: 15, fontWeight: 700, lineHeight: 1.45 }}
          >
            施工リソース・
            <br />
            分業化マネジメント
          </Typography>
          <Typography sx={{ color: tokens.sidebarMu, fontSize: 12 }}>情報通信部門</Typography>
        </Box>

        {isDesktop ? (
          // PC: メニューだけが縦にスクロールする。表と同じ常時表示のカプセル形スクロールバー(あふれるときだけ出る)
          <Box
            sx={{
              flex: '1 1 0',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              // スクロールバーを nav の右端に付ける: nav の右余白(14px)を打ち消し、項目側に同じ余白を戻す
              mr: '-14px',
              // スクロールバーが出ているあいだは SimpleBar が右に 10px の余白を足すので、項目の右余白をその分(14→4px)減らし、
              // 出る・出ないで項目の幅が変わらないようにする
              '& .simplebar-scrollable-y [data-sidebar-menu-items]': { pr: '4px' },
            }}
          >
            <TableScroll
              axis="y"
              fill
              variant="plain"
              tone="sidebar"
              ariaLabel="メニュー"
              scrollTestId="sidebar-menu"
            >
              <Box
                data-sidebar-menu-items
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px', pr: '14px' }}
              >
                {menuSections}
              </Box>
            </TableScroll>
          </Box>
        ) : (
          // スマホ: 横並びの帯。項目は nav の直下に並べる(包まない)
          menuSections
        )}

        {/* 下部(説明とユーザー情報)。PC ではメニュー領域の外にあり、スクロールしても常に見える */}
        <Box
          data-testid="sidebar-footer"
          sx={{ display: { xs: 'contents', sm: 'block' }, flex: 'none' }}
        >
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              color: tokens.sidebarDim,
              fontSize: 11.5,
              lineHeight: 1.6,
              px: 1,
              pt: '12px',
            }}
          >
            業務マスタ・推進スケジュールは「分業化業務検討資料
            総括表（10/2）」を取込。人員・案件・工数は動作確認用の仮データ。
          </Typography>
          <UserBox />
        </Box>
      </Box>

      <Box
        component="main"
        ref={mainRef}
        sx={{
          position: 'relative',
          overflow: 'auto',
          p: { xs: '18px 14px 50px', sm: '26px 30px 60px' },
        }}
      >
        {navigating ? (
          <LoadingStatus sx={{ height: '100%' }}>
            <PageSkeleton />
          </LoadingStatus>
        ) : (
          <Outlet />
        )}
      </Box>
      <DrawerHost />
    </Box>
  );
};
